import { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ThemeContext } from 'styled-components';

import avatar from 'assets/images/avatar.png';
import { status } from 'constants/general';
import postsStrings from 'modules/posts/strings';
import {
  fileUploaderIsolatedFileTypeEnum,
  staticDataLanguageDetailsIdEnum,
} from 'modules/common/enums';
import MultiSelectionDropdownMenu from 'modules/common/components/MultiSelectionDropdownMenu';
import FileUploaderIsolated from 'modules/common/components/FileUploaderIsolated';
import Loader from 'modules/common/components/Loader';
import Popup from 'modules/common/components/Popup';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  LoaderWrapper,
  AddPostWrapper,
  FormWrapper,
  FormTitle,
  FormGrid,
  FormLabel,
  FormTextAreaInput,
  FormFieldWrapper,
  FormMedia,
  FormValidationError,
  FormUploaderErrorText,
  FormUploaderBtnWrapper,
  FormUploaderBtn,
  FormBtnWrapper,
  FormBtn,
  /* ####################### */
  FileItemWrapper,
  FileItemPadding,
  FileImg,
  ResizedFileImg,
  VideoIcon,
  ImgDeleteOverlay,
  ImgDeleteIcon,
  FileItem,
  ImgPreview,
} from './AddPostComponent.style';

const MediaFile = ({
  index,
  fileData,
  isPreviewFilePopupOpen,
  previewFileUrl,
  handlePreviewFileClick,
  handleDeleteFileIconClick,
}) => {
  const { fileTypeId, dataUrl, isProcessed, dimensions } = fileData;
  const { dimenParamToChange, ratio } = dimensions;

  const imgSrc = dataUrl || avatar;
  let fileOpacity = (isProcessed && 0.6) || 0.5;
  let fileBlur = (isProcessed && 3) || 0;
  if (fileTypeId === fileUploaderIsolatedFileTypeEnum.video) {
    fileOpacity = 1;
    fileBlur = 0;
  }

  return (
    <>
      <FileItemWrapper>
        <FileItem onClick={() => handlePreviewFileClick(true, fileData)}>
          <FileItemPadding />
          <FileImg src={imgSrc} opacity={fileOpacity} blur={fileBlur} />
          {fileTypeId === fileUploaderIsolatedFileTypeEnum.image && (
            <ResizedFileImg src={imgSrc} dimenParamToChange={dimenParamToChange} ratio={ratio} />
          )}
          {fileTypeId === fileUploaderIsolatedFileTypeEnum.video && isProcessed && <VideoIcon />}
          <ImgDeleteOverlay />
          <ImgDeleteIcon onClick={event => handleDeleteFileIconClick(event, index)} />
        </FileItem>
      </FileItemWrapper>
      {isPreviewFilePopupOpen && (
        <Popup handleCloseBtnClick={() => handlePreviewFileClick(false, fileData)}>
          <ImgPreview src={previewFileUrl} />
        </Popup>
      )}
    </>
  );
};
MediaFile.propTypes = {
  index: PropTypes.number.isRequired,
  fileData: PropTypes.shape({
    fileTypeId: PropTypes.number,
    dataUrl: PropTypes.string,
    shouldBeProcessed: PropTypes.bool,
    isProcessed: PropTypes.bool,
    dimensions: PropTypes.shape({
      dimenParamToChange: PropTypes.string,
      ratio: PropTypes.number,
    }),
  }).isRequired,
  isPreviewFilePopupOpen: PropTypes.bool.isRequired,
  previewFileUrl: PropTypes.string.isRequired,
  handlePreviewFileClick: PropTypes.func.isRequired,
  handleDeleteFileIconClick: PropTypes.func.isRequired,
};

/* ############################################################################### */
/* ############################################################################### */
/* ############################################################################### */

const AddPostComponent = ({
  formFieldsValues,
  formFieldsValidation,
  uploadMediaErrorMessage,
  shouldShowErrorMessage,
  isPreviewFilePopupOpen,
  previewFileUrl,
  handleTextInputFieldChange,
  handleMultiSelectionDropdownFieldChange,
  handleUploadFileChange,
  handleUploadFileErrorChange,
  handlePreviewFileClick,
  handleDeleteFileIconClick,
  handleAddOrEditPostBtnClick,
}) => {
  const { companyProfilesList, staticData, addOrEditPost } = useSelector(state => ({
    staticData: state.common.staticData,
    companyProfilesList: state.common.companyProfilesList,
    addOrEditPost: state.posts.addOrEditPost,
  }));

  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;

  const { id: postKey } = useParams();

  const formBtnText =
    (postKey && postsStrings.addPost.formEditBtnText) || postsStrings.addPost.formAddBtnText;

  const topicsDropdownData = useMemo(
    () =>
      staticData.details?.interests?.map(interest => ({
        key: interest.interestKey,
        name: interest.languageDetails.find(
          languageDetail => languageDetail.languageId === staticDataLanguageDetailsIdEnum.english,
        ).name,
      })),
    [staticData],
  );

  if (
    staticData.status === status.SHOULD_CALL_API ||
    staticData.status === status.FETCHING ||
    companyProfilesList.status === status.SHOULD_CALL_API ||
    companyProfilesList.status === status.FETCHING
  )
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );

  return (
    <AddPostWrapper>
      <FormWrapper>
        <FormTitle>{postsStrings.addPost.formTitle}</FormTitle>
        <SectionDivider gap={16} withSeparator separatorGap={16} borderColor={colors.grey004} />
        <FormGrid>
          {/* Text Field */}
          <FormFieldWrapper fullWidth>
            <FormLabel>{postsStrings.addPost.formTextFieldLabel}</FormLabel>
            <SectionDivider gap={8} />
            <FormTextAreaInput
              value={formFieldsValues.text}
              maxLength="3000"
              onChange={event => handleTextInputFieldChange('text', event)}
            />
          </FormFieldWrapper>

          {/* Topic Field */}
          <FormFieldWrapper fullWidth>
            <FormLabel>{postsStrings.addPost.formTopicFieldLabel}</FormLabel>
            <SectionDivider gap={8} />
            <MultiSelectionDropdownMenu
              dropdownMenuItemsList={topicsDropdownData}
              dropdownMenuSelectedArr={formFieldsValues.topic}
              dropdownMenuPlaceholder={postsStrings.addPost.formTopicFieldPlaceholder}
              handleDropdownMenuChange={value =>
                handleMultiSelectionDropdownFieldChange('topic', value)
              }
            />
            {formFieldsValidation.topic === false && (
              <>
                <SectionDivider gap={4} />
                <FormValidationError>
                  {postsStrings.addPost.formFieldErrorText(
                    postsStrings.addPost.formTopicFieldLabel,
                  )}
                </FormValidationError>
              </>
            )}
          </FormFieldWrapper>

          {/* Media Field */}
          <FormFieldWrapper fullWidth>
            <FormLabel>{postsStrings.addPost.formMediaFieldLabel}</FormLabel>
            <SectionDivider gap={25} />
            <FormMedia>
              {formFieldsValues.media.uploadedFiles.map((fileData, index) => (
                <MediaFile
                  key={fileData.id}
                  index={index}
                  fileData={fileData}
                  isPreviewFilePopupOpen={isPreviewFilePopupOpen}
                  previewFileUrl={previewFileUrl}
                  handlePreviewFileClick={handlePreviewFileClick}
                  handleDeleteFileIconClick={handleDeleteFileIconClick}
                />
              ))}
            </FormMedia>
            <SectionDivider gap={15} />
            {uploadMediaErrorMessage && (
              <>
                <FormUploaderErrorText>{uploadMediaErrorMessage}</FormUploaderErrorText>
                <SectionDivider gap={15} />
              </>
            )}
            <FileUploaderIsolated
              shouldAllowMultipleFiles
              shouldAllowSingleFileType
              supportedMimeTypesRegex={/image|video/i}
              fileInputAcceptAttrRegex="image/*, video/*"
              mimeTypesThatForcesOneFileOnlyRegex={/video/i}
              filesCountLimit={5}
              filesSizeLimitInMB={1000}
              indexToPrepend={1}
              uploadedData={formFieldsValues.media}
              setUploadedData={handleUploadFileChange}
              setErrorMessage={handleUploadFileErrorChange}
            >
              <FormUploaderBtnWrapper>
                <FormUploaderBtn as="label" htmlFor={`file-uploader-btn-${1}`}>
                  {postsStrings.addPost.formMediaFieldBtnText}
                </FormUploaderBtn>
              </FormUploaderBtnWrapper>
            </FileUploaderIsolated>
            {formFieldsValidation.content === false && (
              <>
                <SectionDivider gap={20} />
                <FormValidationError centerText>
                  {postsStrings.addPost.formContentValidationErrorText}
                </FormValidationError>
              </>
            )}
          </FormFieldWrapper>
        </FormGrid>
        {shouldShowErrorMessage === true && (
          <>
            <SectionDivider gap={15} />
            <FormValidationError centerText>
              {postsStrings.addPost.requestErrorMessage}
            </FormValidationError>
          </>
        )}
        <SectionDivider gap={35} />
        <FormBtnWrapper>
          <FormBtn as="button" onClick={handleAddOrEditPostBtnClick}>
            {(addOrEditPost.status === status.FETCHING && (
              <Loader width={14} height={14} thickness={4} />
            )) ||
              formBtnText}
          </FormBtn>
        </FormBtnWrapper>
      </FormWrapper>
    </AddPostWrapper>
  );
};

AddPostComponent.propTypes = {
  formFieldsValues: PropTypes.shape({
    text: PropTypes.string,
    topic: PropTypes.arrayOf(PropTypes.shape({})),
    media: PropTypes.shape({
      uploadedFiles: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  formFieldsValidation: PropTypes.shape({
    content: PropTypes.bool,
    topic: PropTypes.bool,
  }).isRequired,
  uploadMediaErrorMessage: PropTypes.string.isRequired,
  shouldShowErrorMessage: PropTypes.bool.isRequired,
  isPreviewFilePopupOpen: PropTypes.bool.isRequired,
  previewFileUrl: PropTypes.string.isRequired,
  handleTextInputFieldChange: PropTypes.func.isRequired,
  handleMultiSelectionDropdownFieldChange: PropTypes.func.isRequired,
  handleUploadFileChange: PropTypes.func.isRequired,
  handleUploadFileErrorChange: PropTypes.func.isRequired,
  handlePreviewFileClick: PropTypes.func.isRequired,
  handleDeleteFileIconClick: PropTypes.func.isRequired,
  handleAddOrEditPostBtnClick: PropTypes.func.isRequired,
};

export default AddPostComponent;
